import { CameraControlsImpl } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'
import { Box3, MathUtils, Object3D, Vector3 } from 'three'

import { useLevelGeometry } from '@hooks'
import { GamePhase, useGameStore } from '@stores'

export function TitleCameraRig() {
  const { scene, controls, size: windowSize } = useThree()
  const phase = useGameStore(state => state.phase)
  const { size } = useLevelGeometry()
  const hasZoomedIn = useRef(false)
  const creditsRef = useRef<Object3D>(null)

  // eslint-disable-next-line react-hooks/immutability
  useEffect(() => {
    if (!controls) return
    if (size.visibleWidth === 0 || size.height === 0) return

    const cc = controls as CameraControlsImpl

    if (phase === GamePhase.TITLE || phase === GamePhase.LEVEL_SELECTION) {
      const isFirstRender = !hasZoomedIn.current
      if (isFirstRender) {
        cc.rotatePolarTo(MathUtils.degToRad(45))
        cc.rotateAzimuthTo(MathUtils.degToRad(-25))

        hasZoomedIn.current = true
        cc.zoomTo(windowSize.height * (30 / 1080), false)
        const defaultSmoothTime = cc.smoothTime
        // eslint-disable-next-line react-hooks/immutability
        cc.smoothTime = 1.5
        setTimeout(() => {
          cc.smoothTime = defaultSmoothTime
        }, 5000)
      }

      const center = new Vector3(size.centerOffset, 0, 0)
      const boxSize = new Vector3(
        size.visibleWidth,
        phase === GamePhase.TITLE ? 0.1 : 2,
        size.height,
      )
      const box = new Box3().setFromCenterAndSize(center, boxSize)

      cc.fitToBox(box, true, {
        paddingTop: Math.max(0, size.height - 1),
        paddingBottom: Math.max(0, size.height - 2),
        paddingLeft: 1.5,
        paddingRight: 1.5,
      })
      cc.rotatePolarTo(MathUtils.degToRad(45), true)
      cc.rotateAzimuthTo(MathUtils.degToRad(-45), true)
    } else if (phase === GamePhase.CREDITS) {
      if (!creditsRef.current) {
        scene.traverse(object => {
          if (object.name === 'credits') {
            creditsRef.current = object
          }
        })
      }

      cc.fitToBox(creditsRef.current!, true)
      cc.rotatePolarTo(MathUtils.degToRad(60), true)
      cc.rotateAzimuthTo(MathUtils.degToRad(-25), true)
    }
  }, [windowSize, controls, size, phase, scene])

  return null
}
